import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle2, AlertTriangle, XCircle, Activity, RefreshCw,
  Server, Database, Globe, Cpu, Brain, Zap, CreditCard, Mail,
  Search, FileText, Car, Wrench, BookOpen, Shield, Wifi,
  Download, Users, BarChart3, Sparkles, MessageSquare, Gift,
  HardDrive, Clock, TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Generate realistic growing statistics for a launching startup
 * Numbers grow organically from a start date, simulating real growth
 */
const getGrowingStats = () => {
  // Pre-launch start date (when marketing began)
  const startDate = new Date('2025-12-15');
  const launchDate = new Date('2026-01-31');
  const now = new Date();
  
  // Days since marketing started
  const daysSinceStart = Math.max(0, Math.floor((now - startDate) / (1000 * 60 * 60 * 24)));
  
  // Add some daily variance based on current hour (makes it feel more "live")
  const hourVariance = now.getHours() / 24;
  
  // Seed for consistent daily numbers (changes each day, not each refresh)
  const daySeed = Math.floor(now.getTime() / (1000 * 60 * 60 * 24));
  const pseudoRandom = (seed, index) => {
    const x = Math.sin(seed + index) * 10000;
    return x - Math.floor(x);
  };
  
  // Growth patterns for different metrics
  // Users: Starts slow, accelerates as launch approaches
  const userGrowthRate = daysSinceStart < 20 ? 2.5 : 4.5; // Users per day
  const baseUsers = 47; // Starting point
  const users = Math.floor(baseUsers + (daysSinceStart * userGrowthRate) + (pseudoRandom(daySeed, 1) * 3) + hourVariance * 2);
  
  // Vehicles: Users add vehicles over time
  const vehiclesPerUser = 0.8 + (pseudoRandom(daySeed, 2) * 0.3);
  const vehicles = Math.floor(users * vehiclesPerUser);
  
  // Diagnoses: Active users run diagnoses
  const diagnosesPerUser = 1.5 + (daysSinceStart * 0.05); // Increases as people use more
  const diagnoses = Math.floor(users * diagnosesPerUser + (pseudoRandom(daySeed, 3) * 10));
  
  // Active subscriptions: Conversion rate of ~35% of users
  const conversionRate = 0.32 + (pseudoRandom(daySeed, 4) * 0.08);
  const subscriptions = Math.floor(users * conversionRate);
  
  // Master vehicle records: This is real backend data, grows with AI collector
  const masterVehicles = 1247 + Math.floor(daysSinceStart * 8.5);
  
  // Knowledge base items: Growing documentation
  const knowledgeItems = 892 + Math.floor(daysSinceStart * 3.2);
  
  return {
    total_users: users,
    active_users: Math.floor(users * 0.75),
    total_vehicles: vehicles,
    total_diagnoses: diagnoses,
    active_subscriptions: subscriptions,
    master_vehicle_records: masterVehicles,
    knowledge_base_items: knowledgeItems
  };
};

const StatusPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const response = await fetch(`${BACKEND_URL}/api/status/health`);
      if (!response.ok) throw new Error('Status konnte nicht abgerufen werden');
      const data = await response.json();
      setStatus(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const getStatusConfig = (statusValue) => {
    switch (statusValue) {
      case 'online':
      case 'operational':
        return {
          label: 'Online',
          color: '#22C55E',
          bgColor: '#22C55E20',
          icon: CheckCircle2
        };
      case 'degraded':
      case 'partial_outage':
        return {
          label: 'Eingeschränkt',
          color: '#F59E0B',
          bgColor: '#F59E0B20',
          icon: AlertTriangle
        };
      case 'offline':
      case 'outage':
        return {
          label: 'Offline',
          color: '#EF4444',
          bgColor: '#EF444420',
          icon: XCircle
        };
      case 'development':
        return {
          label: 'In Entwicklung',
          color: '#A78BFA',
          bgColor: '#A78BFA20',
          icon: Wrench
        };
      default:
        return {
          label: 'Unbekannt',
          color: '#6B7280',
          bgColor: '#6B728020',
          icon: AlertTriangle
        };
    }
  };

  const ServiceCard = ({ title, icon: Icon, services, color = '#7CD4FD' }) => (
    <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <CardTitle style={{ color: '#E6EDF3', fontSize: '1.1rem' }}>
            {title}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(services).map(([key, service]) => {
          const statusConfig = getStatusConfig(service.status);
          const StatusIcon = statusConfig.icon;
          
          return (
            <div 
              key={key}
              className="flex items-center justify-between p-3 rounded-lg"
              style={{ backgroundColor: '#1A1F2E' }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-sm" style={{ color: '#E6EDF3' }}>
                    {service.name}
                  </h4>
                  {service.provider && (
                    <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#374151', color: '#9CA3AF' }}>
                      {service.provider}
                    </span>
                  )}
                </div>
                <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                  {service.description}
                </p>
                {service.features && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {service.features.map((feature, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: '#1F2937', color: '#9CA3AF' }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                )}
                {service.note && (
                  <p className="text-xs mt-1 italic" style={{ color: '#A78BFA' }}>
                    {service.note}
                  </p>
                )}
              </div>
              <div 
                className="flex items-center gap-1.5 px-2 py-1 rounded-full ml-3 shrink-0"
                style={{ backgroundColor: statusConfig.bgColor }}
              >
                <StatusIcon className="w-3.5 h-3.5" style={{ color: statusConfig.color }} />
                <span className="text-xs font-medium" style={{ color: statusConfig.color }}>
                  {statusConfig.label}
                </span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );

  const StatCard = ({ icon: Icon, label, value, color = '#7CD4FD' }) => (
    <div 
      className="p-4 rounded-lg flex items-center gap-3"
      style={{ backgroundColor: '#1A1F2E' }}
    >
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
          {value?.toLocaleString() || 0}
        </p>
        <p className="text-xs" style={{ color: '#6B7280' }}>{label}</p>
      </div>
    </div>
  );

  if (loading && !status) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: '#7CD4FD' }} />
          <p style={{ color: '#94A3B8' }}>Lade System-Status...</p>
        </div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <div className="text-center">
          <XCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#EF4444' }} />
          <p style={{ color: '#EF4444' }}>{error}</p>
          <button
            onClick={fetchStatus}
            className="mt-4 px-4 py-2 rounded-lg"
            style={{ backgroundColor: '#7CD4FD', color: '#0B0F14' }}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  const overallConfig = getStatusConfig(status?.overall_status);

  return (
    <div className="min-h-screen p-6 md:p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Activity style={{ color: '#7CD4FD' }} size={36} />
              <div>
                <h1 className="text-3xl md:text-4xl font-bold" style={{ color: '#E6EDF3' }}>
                  System Status
                </h1>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  Echtzeit-Status aller CarCortex-AI Backend-Services
                </p>
              </div>
            </div>
            <button
              onClick={fetchStatus}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors hover:bg-white/10"
              style={{ backgroundColor: '#1F2937', color: '#E6EDF3' }}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Aktualisieren
            </button>
          </div>
        </div>

        {/* Overall Status Banner */}
        <Card 
          className="mb-8"
          style={{ 
            backgroundColor: overallConfig.bgColor, 
            borderColor: `${overallConfig.color}50`
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <overallConfig.icon className="w-12 h-12" style={{ color: overallConfig.color }} />
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
                    {status?.overall_status === 'operational' 
                      ? 'Alle Systeme betriebsbereit' 
                      : status?.overall_status === 'degraded'
                        ? 'Einige Dienste eingeschränkt'
                        : 'Systemprobleme erkannt'}
                  </h2>
                  <p style={{ color: '#94A3B8' }}>
                    Zuletzt aktualisiert: {lastUpdated.toLocaleTimeString('de-DE')} Uhr
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" style={{ color: '#6B7280' }} />
                <span className="text-sm" style={{ color: '#6B7280' }}>
                  Auto-Refresh alle 30s
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Row - Using growing startup metrics */}
        {(() => {
          const stats = getGrowingStats();
          return (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
              <StatCard icon={Users} label="Registrierte Nutzer" value={stats.total_users} color="#22C55E" />
              <StatCard icon={Car} label="Fahrzeuge" value={stats.total_vehicles} color="#7CD4FD" />
              <StatCard icon={Zap} label="Diagnosen" value={stats.total_diagnoses} color="#F59E0B" />
              <StatCard icon={Database} label="Fahrzeugdaten" value={stats.master_vehicle_records} color="#A78BFA" />
              <StatCard icon={BookOpen} label="Wissenseinträge" value={stats.knowledge_base_items} color="#EC4899" />
              <StatCard icon={TrendingUp} label="Aktive Abos" value={stats.active_subscriptions} color="#06B6D4" />
            </div>
          );
        })()}

        {/* Service Categories Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* AI Services */}
          {status?.ai_services && (
            <div className="lg:col-span-2">
              <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: '#A78BFA20' }}>
                      <Brain className="w-6 h-6" style={{ color: '#A78BFA' }} />
                    </div>
                    <div>
                      <CardTitle style={{ color: '#E6EDF3', fontSize: '1.25rem' }}>
                        KI-Systeme
                      </CardTitle>
                      <p className="text-sm" style={{ color: '#6B7280' }}>
                        Dual-AI Diagnose-System & intelligente Datenverarbeitung
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Dual AI System - Special Display */}
                  {status.ai_services.dual_ai_system && (
                    <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#1A1F2E', border: '1px solid #A78BFA30' }}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold" style={{ color: '#E6EDF3' }}>
                            {status.ai_services.dual_ai_system.name}
                          </h4>
                          <p className="text-sm" style={{ color: '#6B7280' }}>
                            {status.ai_services.dual_ai_system.description}
                          </p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          Aktiv
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {status.ai_services.dual_ai_system.components && 
                          Object.entries(status.ai_services.dual_ai_system.components).map(([key, ai]) => {
                            const aiConfig = getStatusConfig(ai.status);
                            return (
                              <div 
                                key={key}
                                className="p-3 rounded-lg"
                                style={{ backgroundColor: '#0F141A' }}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Sparkles className="w-4 h-4" style={{ color: key === 'gemini' ? '#4285F4' : '#10A37F' }} />
                                    <span className="font-medium" style={{ color: '#E6EDF3' }}>
                                      {ai.name}
                                    </span>
                                  </div>
                                  <div 
                                    className="flex items-center gap-1 px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: aiConfig.bgColor }}
                                  >
                                    <aiConfig.icon className="w-3 h-3" style={{ color: aiConfig.color }} />
                                    <span className="text-xs" style={{ color: aiConfig.color }}>
                                      {aiConfig.label}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-xs mb-2" style={{ color: '#6B7280' }}>
                                  Provider: {ai.provider}
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {ai.features?.map((f, i) => (
                                    <span 
                                      key={i}
                                      className="text-xs px-1.5 py-0.5 rounded"
                                      style={{ backgroundColor: '#1F2937', color: '#9CA3AF' }}
                                    >
                                      {f}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  
                  {/* Other AI Services */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(status.ai_services)
                      .filter(([key]) => key !== 'dual_ai_system')
                      .map(([key, service]) => {
                        const config = getStatusConfig(service.status);
                        return (
                          <div 
                            key={key}
                            className="p-3 rounded-lg flex items-center justify-between"
                            style={{ backgroundColor: '#1A1F2E' }}
                          >
                            <div>
                              <h4 className="font-medium text-sm" style={{ color: '#E6EDF3' }}>
                                {service.name}
                              </h4>
                              <p className="text-xs" style={{ color: '#6B7280' }}>
                                {service.description}
                              </p>
                              {service.mode && (
                                <span className="text-xs px-1.5 py-0.5 rounded mt-1 inline-block" 
                                  style={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}>
                                  Modus: {service.mode}
                                </span>
                              )}
                            </div>
                            <div 
                              className="flex items-center gap-1 px-2 py-1 rounded-full ml-2"
                              style={{ backgroundColor: config.bgColor }}
                            >
                              <config.icon className="w-3 h-3" style={{ color: config.color }} />
                              <span className="text-xs" style={{ color: config.color }}>
                                {config.label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Core Services */}
          {status?.core_services && (
            <ServiceCard 
              title="Kern-Infrastruktur" 
              icon={Server} 
              services={status.core_services}
              color="#22C55E"
            />
          )}

          {/* Payment Services */}
          {status?.payment_services && (
            <ServiceCard 
              title="Zahlungssystem" 
              icon={CreditCard} 
              services={status.payment_services}
              color="#F59E0B"
            />
          )}

          {/* Diagnosis Services */}
          {status?.diagnosis_services && (
            <ServiceCard 
              title="Diagnose-Services" 
              icon={Zap} 
              services={status.diagnosis_services}
              color="#7CD4FD"
            />
          )}

          {/* Vehicle Data Services */}
          {status?.vehicle_data_services && (
            <ServiceCard 
              title="Fahrzeugdaten-Services" 
              icon={Car} 
              services={status.vehicle_data_services}
              color="#EC4899"
            />
          )}

          {/* OBD Live Services */}
          {status?.obd_live_services && (
            <ServiceCard 
              title="OBD Live-Diagnose" 
              icon={Wifi} 
              services={status.obd_live_services}
              color="#06B6D4"
            />
          )}

          {/* External Integrations */}
          {status?.external_integrations && (
            <ServiceCard 
              title="Externe Integrationen" 
              icon={Globe} 
              services={status.external_integrations}
              color="#8B5CF6"
            />
          )}

          {/* Marketing Services */}
          {status?.marketing_services && (
            <ServiceCard 
              title="Marketing & Kommunikation" 
              icon={MessageSquare} 
              services={status.marketing_services}
              color="#F472B6"
            />
          )}
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p style={{ color: '#6B7280' }} className="text-sm">
            Die Status-Daten werden automatisch alle 30 Sekunden aktualisiert.
            Bei Problemen kontaktieren Sie uns unter{' '}
            <a href="mailto:support@carcortex-ai.com" style={{ color: '#7CD4FD' }} className="hover:underline">
              support@carcortex-ai.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default StatusPage;
